import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {Auth} from '../actions/auth.actions';
import {CreditNotes} from '../actions/creditNote.actions';
import ClearState = Auth.ClearState;
import GetCreditNotes = CreditNotes.GetCreditNotes;
import GetCreditNote = CreditNotes.GetCreditNote;
import CreateCreditNote = CreditNotes.CreateCreditNote;
import UpdateCreditNote = CreditNotes.UpdateCreditNote;
import DeleteCreditNote = CreditNotes.DeleteCreditNote;
import {CreditNote} from '../models/credit.note/creditNote.model';
import {CreditNoteService} from '../services/creditNote.service';

export interface CreditNoteStateModel {
  creditNotes: Array<CreditNote>;
  currentCreditNote: CreditNote;
}

@State<CreditNoteStateModel>({
  name: 'creditNote',
  defaults: {
    creditNotes: [],
    currentCreditNote: null
  }
})
@Injectable()
export class CreditNoteState {
  constructor(private creditNoteService: CreditNoteService) {
  }

  @Selector()
  static creditNotes(state: CreditNoteStateModel): Array<CreditNote> {
    return state.creditNotes;
  }

  @Selector()
  static creditNote(state: CreditNoteStateModel): CreditNote {
    return state.currentCreditNote;
  }

  @Action(GetCreditNotes)
  getCreditNotes(ctx: StateContext<CreditNoteStateModel>, action: GetCreditNotes): Observable<CreditNote[]> {
    if (!action.force) {
      const creditNotes = ctx.getState().creditNotes;
      if (creditNotes.length > 0) {
        return of(creditNotes);
      }
    }
    return this.creditNoteService.getCreditNotes().pipe(
      tap(res => {
        ctx.patchState({
          creditNotes: res.creditNotes,
        });
      }),
      map(res => res.creditNotes)
    );
  }

  @Action(GetCreditNote)
  getCreditNote(ctx: StateContext<CreditNoteStateModel>, action: GetCreditNote): Observable<CreditNote> {
    if (!action.force) {
      const creditNote = ctx.getState().currentCreditNote;
      if (creditNote && creditNote.id === action.id) {
        return of(creditNote);
      }

      const cachedCreditNote = ctx.getState().creditNotes.find(p => p.id === action.id);
      if (cachedCreditNote) {
        ctx.patchState({
          currentCreditNote: cachedCreditNote
        });
        return of(cachedCreditNote);
      }
    }
    return this.creditNoteService.getCreditNote(action.id).pipe(
      tap(creditNote => {
        ctx.patchState({
          currentCreditNote: creditNote
        });
      })
    );
  }

  @Action(CreateCreditNote)
  createCreditNote(ctx: StateContext<CreditNoteStateModel>, action: CreateCreditNote): Observable<CreditNote> {
    return this.creditNoteService.createCreditNote(action.creditNote).pipe(
      tap(creditNote => {
        ctx.patchState({
          currentCreditNote: creditNote
        });
      })
    );
  }

  @Action(UpdateCreditNote)
  updateCreditNote(ctx: StateContext<CreditNoteStateModel>, action: UpdateCreditNote): Observable<CreditNote> {
    return this.creditNoteService.updateCreditNote(action.creditNote).pipe(
      tap(creditNote => {
        ctx.patchState({
          currentCreditNote: creditNote
        });
      })
    );
  }

  @Action(DeleteCreditNote)
  deleteCreditNote(ctx: StateContext<CreditNoteStateModel>, action: DeleteCreditNote): Observable<CreditNote> {
    const creditNotes = ctx.getState().creditNotes;
    const index = creditNotes.indexOf(action.creditNote);
    return this.creditNoteService.deleteCreditNote(action.creditNote.id).pipe(
      tap(creditNote => {
        ctx.patchState({
          currentCreditNote: null,
          creditNotes: [
            ...creditNotes.slice(0, index),
            ...index === creditNotes.length - 1 ? [] : creditNotes.slice(index + 1)
          ]
        });
      })
    );
  }


  @Action(ClearState)
  clearState(ctx: StateContext<CreditNoteStateModel>): void {
    ctx.setState({
      creditNotes: [],
      currentCreditNote: null
    });
  }
}
